// Global Instructions Rule Applied!
//
// Configuration → Internal Agents → Investment Criteria.
//
// The criteria the firm judges deals against. They are PROMPT MATERIAL: the
// deal follower reads the active rows (filtered to the deal's type) as its
// EVALUATION CRITERIA block and walks them in the Deal Brief's criteria-fit
// section. The catalogue is human-owned — an agent that notices a missing
// factor fires a criteria_suggestion signal; it never writes here.
//
// Same chrome as the rest of the panel: FormSection + AntD Table, edits
// through a modal form, writes via narrow admin RPCs (is_app_admin in SQL).
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useLookups } from '../../global_configs/hooks/useLookups.ts'
import {
  fetchInvestmentCriteria,
  saveInvestmentCriterion,
  deleteInvestmentCriterion
} from '../../internal_agents/services/internalAgentsService.ts'
import { InvestmentCriterion } from '../../../state_manager/data_models/internal_agents_model.ts'

const { Text } = Typography

const ALL_DEAL_TYPES = '__all__'

interface CriterionFormValues {
  criterion_key: string
  label: string
  guidance?: string
  priority: number
  deal_type_id: string
  active: boolean
}

export const InvestmentCriteriaSection: React.FC = () => {
  const [rows, setRows] = useState<InvestmentCriterion[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<InvestmentCriterion | null>(null)
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<CriterionFormValues>()
  const { getLookupOptions } = useLookups()
  // Call-time thunk, not a render-time snapshot — see FollowerAgentsPanel:
  // a token captured at mount outlives the JWT and surfaces as 403s once
  // the panel has been open for over an hour.
  const token = () => getAccessToken(store.getState().auth)

  const dealTypeOptions = useMemo(
    () => [
      { value: ALL_DEAL_TYPES, label: 'All deal types' },
      ...getLookupOptions('Deal Type').map((o) => ({ value: o.value, label: o.label }))
    ],
    [getLookupOptions]
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchInvestmentCriteria(token()))
    } catch (error) {
      console.error('[internal-agents] investment criteria load failed:', error)
      showErrorToast(error instanceof Error ? error.message : 'Failed to load investment criteria')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openEditor = (row: InvestmentCriterion | null) => {
    setEditing(row)
    setCreating(row === null)
    form.setFieldsValue({
      criterion_key: row?.investment_criteria__criterion_key ?? '',
      label: row?.investment_criteria__label ?? '',
      guidance: row?.investment_criteria__guidance ?? '',
      priority: row?.investment_criteria__priority ?? 100,
      deal_type_id: row?.investment_criteria__deal_type_id ?? ALL_DEAL_TYPES,
      active: row?.investment_criteria__active ?? true
    })
  }

  const closeEditor = () => {
    setEditing(null)
    setCreating(false)
    form.resetFields()
  }

  const handleSave = async () => {
    let values: CriterionFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    try {
      const isAll = values.deal_type_id === ALL_DEAL_TYPES
      await saveInvestmentCriterion(
        {
          id: editing?.investment_criteria__id ?? null,
          criterion_key: values.criterion_key.trim(),
          label: values.label.trim(),
          guidance: values.guidance ?? '',
          priority: values.priority,
          deal_type_id: isAll ? null : values.deal_type_id,
          clear_deal_type: isAll,
          active: values.active
        },
        token()
      )
      closeEditor()
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to save criterion')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: InvestmentCriterion) => {
    try {
      await deleteInvestmentCriterion(row.investment_criteria__id, token())
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to delete criterion')
    }
  }

  const handleToggleActive = async (row: InvestmentCriterion, active: boolean) => {
    try {
      await saveInvestmentCriterion(
        { id: row.investment_criteria__id, active },
        token()
      )
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to update criterion')
      await load()
    }
  }

  const columns = [
    {
      title: 'Criterion',
      dataIndex: 'investment_criteria__label',
      render: (label: string, row: InvestmentCriterion) => (
        <span>
          <button
            type='button'
            className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left font-medium'
            onClick={() => openEditor(row)}
          >
            {label}
          </button>
          <span className='block font-mono text-[11px] text-gray-400'>
            {row.investment_criteria__criterion_key}
          </span>
        </span>
      )
    },
    {
      title: 'Guidance (prompt text)',
      dataIndex: 'investment_criteria__guidance',
      render: (guidance: string | null) => (
        <span className='text-gray-600 text-[12.5px]'>{guidance || '—'}</span>
      )
    },
    {
      title: 'Deal type',
      dataIndex: 'deal_type_label',
      width: 150,
      render: (label: string | null) =>
        label ? <Tag>{label}</Tag> : <span className='text-gray-400 text-xs'>All</span>
    },
    {
      title: (
        <Tooltip title='Order the criteria appear in the agent’s prompt and in the Deal Brief’s criteria-fit section.'>
          Priority
        </Tooltip>
      ),
      dataIndex: 'investment_criteria__priority',
      width: 90,
      align: 'right' as const
    },
    {
      title: (
        <Tooltip title='Off = the criterion stays on record but is not shown to the agent, so it stops being assessed on the next run.'>
          Active
        </Tooltip>
      ),
      dataIndex: 'investment_criteria__active',
      width: 90,
      render: (active: boolean, row: InvestmentCriterion) => (
        <Switch
          size='small'
          checked={active}
          onChange={(checked) => handleToggleActive(row, checked)}
        />
      )
    },
    {
      title: '',
      width: 50,
      render: (_: unknown, row: InvestmentCriterion) => (
        <Popconfirm
          title='Delete this criterion?'
          description='Deactivating keeps it on record; deleting removes it from the catalogue.'
          okText='Delete'
          okButtonProps={{ danger: true }}
          onConfirm={() => handleDelete(row)}
        >
          <Button size='small' type='text' danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ]

  return (
    <FormSection
      data-component='InvestmentCriteriaSection'
      title='Investment criteria'
      titleColor='blue'
      scrollable={false}
      loading={loading}
      headerActions={
        <span className='flex items-center gap-2'>
          <Text type='secondary' style={{ fontSize: 12 }}>
            What the deal follower judges deals against
          </Text>
          <Button size='small' type='primary' icon={<PlusOutlined />} onClick={() => openEditor(null)}>
            Add criterion
          </Button>
        </span>
      }
    >
      <Table
        size='small'
        rowKey='investment_criteria__id'
        pagination={false}
        dataSource={rows}
        columns={columns}
      />
      <Text type='secondary' style={{ fontSize: 12 }}>
        Changes reach the agent on its next run, after a backend restart picks up the config cache.
        The agent proposes additions through a “criteria suggestion” signal — it never edits this
        list.
      </Text>

      <Modal
        title={creating ? 'Add investment criterion' : 'Edit investment criterion'}
        open={editing != null || creating}
        onCancel={closeEditor}
        onOk={handleSave}
        okText='Save'
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout='vertical' preserve={false}>
          <Form.Item
            name='criterion_key'
            label='Key'
            tooltip='Stable identifier the agent cites. Lower-case with underscores, e.g. terms_valuation.'
            rules={[
              { required: true, message: 'A key is required' },
              {
                pattern: /^[a-z0-9_]+$/,
                message: 'Lower-case letters, digits and underscores only'
              }
            ]}
          >
            <Input placeholder='team' disabled={!creating} />
          </Form.Item>
          <Form.Item
            name='label'
            label='Label'
            rules={[{ required: true, message: 'A label is required' }]}
          >
            <Input placeholder='Team quality' />
          </Form.Item>
          <Form.Item
            name='guidance'
            label='Guidance'
            tooltip='Goes into the agent’s prompt verbatim — say what evidence counts for this criterion.'
          >
            <Input.TextArea
              rows={3}
              placeholder='Founder capability, domain depth, candor, and execution track record.'
            />
          </Form.Item>
          <Form.Item name='deal_type_id' label='Applies to'>
            <Select options={dealTypeOptions} />
          </Form.Item>
          <Form.Item name='priority' label='Priority'>
            <InputNumber min={0} max={10000} className='w-full' />
          </Form.Item>
          <Form.Item name='active' label='Active' valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </FormSection>
  )
}

export default InvestmentCriteriaSection
